import { logger } from '@talos/kyoko';
import type { SerializedDockview } from 'dockview';
import { getLargestLayoutGroup } from '../../../providers/AppLayoutContextProvider/utils/getLargestGroup';
import { getLayoutGroupById } from '../../../providers/AppLayoutContextProvider/utils/getLayoutGroupById';
import { getLayoutGroupByPanelId } from '../../../providers/AppLayoutContextProvider/utils/getLayoutGroupByPanelId';
import type { PanelComponentSpec, PanelType, TypedGroupviewPanelState } from '../../../types/LayoutConfig';

/**
 * Add a panel to the layout
 * @param layout The layout to add the panel to
 * @param addedPanel The panel to add
 * @param groupId The group to add the panel to
 * @param panelId The panel id to add the panel after
 * @returns The layout
 */
export function addPanelToLayout({
  layout,
  addedPanel,
  groupId,
  panelId,
}: {
  layout: SerializedDockview;
  addedPanel: TypedGroupviewPanelState | PanelComponentSpec;
  groupId: string;
  panelId?: string;
}): SerializedDockview {
  let contentComponent: PanelType,
    tabComponent: string | undefined,
    title: string | undefined,
    params: TypedGroupviewPanelState['params'] = {};
  if ('type' in addedPanel) {
    title = addedPanel.label;
    contentComponent = addedPanel.type;
    tabComponent = addedPanel.canBeAdded ? undefined : 'readOnly';
    params = { type: addedPanel.type };
  } else {
    contentComponent = addedPanel.contentComponent;
    tabComponent = addedPanel.tabComponent;
    title = addedPanel.title;
    params = { type: addedPanel.contentComponent, ...addedPanel.params };
  }
  const updatedLayout = structuredClone(layout);
  // Get the group, or the group with the panel, or fallback to the largest group if the group and panel ids are not found
  const targetGroup =
    getLayoutGroupById(updatedLayout, groupId) ??
    (panelId != null ? getLayoutGroupByPanelId(updatedLayout, panelId) : undefined) ??
    getLargestLayoutGroup(updatedLayout);
  if (targetGroup == null) {
    logger.error(new Error(`Could not find a group to add the ${contentComponent} panel to`));
    return updatedLayout;
  }

  if (contentComponent) {
    // Add the panel to the layout, using type as the panel id
    updatedLayout.panels[contentComponent] = {
      id: contentComponent,
      contentComponent,
      tabComponent,
      title,
      params,
    };
    // Add the panel to the targetted group's views
    targetGroup.data.views.push(contentComponent);
  }

  return updatedLayout;
}

/**
 * Add additional panels to the layout
 * @param layout The layout to add the panel to
 * @param panels The panel to add
 * @param groupId The group to add the panel to
 * @returns
 */
export function addPanelsToLayout(
  layout: SerializedDockview,
  panels: (TypedGroupviewPanelState | PanelComponentSpec)[],
  groupId: string
) {
  panels.forEach(addedPanel => {
    layout = addPanelToLayout({
      layout,
      addedPanel,
      groupId,
    });
  });
  return layout;
}
